import React, { useEffect, useRef, useState } from 'react'
import { BasicLayout } from '../../components/BasicLayout'
import { Button } from '../../components/ui/button'
import { Message } from './components/Message'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import { TextInput } from '../../components/inputs/TextInput'
import { CreateAssistantMessageDto, OpenAiThreadMessage } from 'shared'
import { HistoryBox } from './components/HistoryBox'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { QueryKeyEnum } from '../../model/enums/QueryKeyEnum'

interface Props {}

export const AI: React.FC<Props> = (props) => {
  const { threadId } = useParams<{ threadId?: string }>()
  const navigate = useNavigate()
  const lastElementRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    lastElementRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [lastElementRef.current])

  const queryClient = useQueryClient()
  const [receivedMessage, setReceivedMessage] = useState<string | null>(null)
  const [sentMessage, setSentMessage] = useState<string | null>(null)
  const [messages, setMessages] = useState<OpenAiThreadMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (threadId && !isLoading) {
      fetchThreadMessages(threadId)
    }
  }, [threadId, isLoading])

  async function fetchThreadMessages(headersThreadId: string | null) {
    const messagesResponse = await fetch(`/api/ai/assistant/thread/${headersThreadId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
    const messages = await messagesResponse.json()
    setMessages(messages.messages.reverse())
  }

  async function fetchStream(
    createMessage: CreateAssistantMessageDto,
    formikHelpers: FormikHelpers<any>,
  ) {
    formikHelpers.resetForm()
    setIsLoading(true)
    setReceivedMessage('')
    setSentMessage(createMessage.message)

    setTimeout(() => {
      lastElementRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)

    const response = await fetch('/api/ai/assistant', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({
        message: createMessage.message,
        threadId,
      }),
    })

    if (!response.ok) {
      console.error('Network response was not ok')
      return
    }

    if (!response.body) {
      return
    }

    const headersThreadId = response.headers.get('Thread-Id')
    if (headersThreadId && threadId !== headersThreadId) {
      navigate(`/ai/${headersThreadId}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')

    let receivedText = ''
    let done = false

    while (!done) {
      const { value, done: readerDone } = await reader.read()
      done = readerDone

      if (value) {
        const chunk = decoder.decode(value, { stream: !done })
        receivedText += chunk

        // Update state or handle the chunk
        setReceivedMessage((prevMessages) => prevMessages + chunk)
      }
    }
    await fetchThreadMessages(headersThreadId)
    setReceivedMessage(null)
    setSentMessage(null)
    setIsLoading(false)
    queryClient.invalidateQueries(QueryKeyEnum.THREADS)

    setTimeout(() => {
      lastElementRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }

  return (
    <BasicLayout title="Stavebník (AI BETA)">
      <div className="h-[calc(100vh-220px)] grid grid-cols-[minmax(0,_300px)_1fr] gap-4 border rounded-md">
        <div className="border-r p-3 h-[calc(100vh-220px)] overflow-scroll flex flex-col gap-3">
          <Button
            onClick={() => {
              setMessages([])
              navigate('/ai')
            }}
            variant="secondary"
          >
            Nová konverzace
          </Button>
          <HistoryBox />
        </div>
        <div className="p-3 flex flex-col h-[calc(100vh-220px)]">
          <div className="overflow-auto p-10 h-100 flex-grow">
            <ul className="space-y-5 flex flex-col gap-3">
              {messages.map((message) => (
                <Message
                  key={message.id}
                  incoming={message.role === 'assistant'}
                  message={
                    message?.content[0]?.type === 'text' ? message.content[0].text.value : ''
                  }
                />
              ))}
              {sentMessage && <Message incoming={false} message={sentMessage} />}
              {!receivedMessage && isLoading && (
                <div className="mt-2 flex space-x-2 justify-start items-start bg-white h-2">
                  <span className="sr-only">Loading...</span>
                  <div className="h-2 w-2 bg-gray-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="h-2 w-2 bg-gray-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="h-2 w-2 bg-gray-600 rounded-full animate-bounce"></div>
                </div>
              )}
              {receivedMessage && <Message incoming={true} message={receivedMessage} />}
              <li ref={lastElementRef} id="lastElement" />
            </ul>
          </div>
          <div className="mt-5 h-16">
            <Formik initialValues={{ message: '' }} onSubmit={fetchStream}>
              {(formik) => (
                <Form>
                  <div className="w-100 flex items-center gap-3">
                    <div className="flex-1">
                      <Field name="message" component={TextInput} />
                    </div>
                    <Button size="sm" isLoading={isLoading}>
                      Odeslat
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </BasicLayout>
  )
}
